import { Box, Container, Grid, Typography } from "@mui/material";
import { cmsApiClient, CmsApiAcl } from "@cm-websites/common/dist/cms-api";
import { NavigationAndFooterLayout } from "../../layouts";
import { NextPageWithLayout, SingleProduct } from "@cm-websites/common";
import { NextSeo } from "next-seo";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import i18nConfig from "../../../next-i18next.config";
import NextLink from "next/link";
import React from "react";
import type { CmsApiModel } from "@cm-websites/common/dist/cms-api";
import type { GetStaticProps } from "next";
import type { Product } from "@cm-websites/common";

type RecipesProps = { products: Product[] };

export const Recipes: NextPageWithLayout<RecipesProps> = ({ products = [] }) => (
  <React.Fragment>
    <NextSeo
      description="Find menu suggestions and recipe ideas for your Murder Mystery Party. Menu suggestions for all our cases."
      title="Recipe ideas and menu suggestions"
    />
    <Box sx={{ pb: { xs: 8, sm: 10, lg: 14, xl: 16 } }}>
      <Container maxWidth="md" sx={{ pt: { xs: 12, sm: 14, lg: 16 } }}>
        <Typography variant="h1" sx={{ hyphens: "none", pb: 4 }}>
          Recipe ideas and menu suggestions
        </Typography>
        <Typography variant="body1" paragraph>
          A Muder Mystery offers the opportunity to have a relaxed and fun evening with friends, family
          or work colleagues. The food should not be missing either. So that you as the host don't have
          to stand in the kitchen forever, recipes that you can prepare together as a group, for example,
          are best suited. Ideal dishes are e.g. raclette, fondue, wraps, summer rolls or a buffet where
          each guest can bring something. If you have a little more time and desire, then we also
          recommend a classic 3-course meal.
        </Typography>
        <Typography variant="body1" paragraph>
          Ideally, the food should be thematically related to the Murder Mystery case you have chosen.
          The food should match the plot of the mystery dinner. For example, you can create a menu of
          dishes that fit the setting and/or the characters particularly well. For example, Mediterranean
          cuisine with fish goes particularly well with{" "}
          <NextLink href="/murder-mystery-games/murder-overboard">"Murder Overboard"</NextLink>. After
          all, the fiery Italian ship's cook Antonio is cooking on board the luxury yacht!
        </Typography>
        <Typography variant="body1" paragraph>
          We have put together a suitable menu suggestion for your dinner party. You can find them
          directly on the page of the respective case. By the way, there we offer you further additional
          materials such as decoration ideas or costume suggestions. Please note: Our recipes and menu
          suggestions are only suggestions. You can also cook something completely different or opt for a
          buffet or raclette, for example. The plot of the case is not bound to you cooking a certain
          menu.
        </Typography>
      </Container>
      <Container maxWidth="lg" sx={{ pt: 3, pb: 12 }}>
        <Grid
          container
          columnSpacing={{ xs: 3, sm: 4, md: 5, lg: 6 }}
          rowSpacing={{ xs: 4, sm: 5, lg: 6 }}
        >
          {products.map((product) => (
            <Grid key={product.id} item xs={6} sm={4} lg={3}>
              <SingleProduct
                imageAlt={`Cover des Falls „${product.title}“`}
                imageTitle={`Rezeptideen für den Fall „${product.title}“`}
                path={`/recipes/${product.slug}`}
                product={product}
                showPlayerInfo={false}
                showPrice={false}
              />
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="md" sx={{ pb: { xs: 6, sm: 8, lg: 10, xl: 12 } }}>
        <Typography variant="h2" sx={{ hyphens: "none", pb: 6 }}>
          When is the food served at a Murder Mystery Party?
        </Typography>

        <Typography variant="body1" paragraph>
          Basically, there are two ways in which you can integrate the food into the Murder Mystery.
          Which option you choose depends on your personal preferences and the food that is served.
        </Typography>

        <Typography variant="h3" sx={{ hyphens: "none", pt: 3, pb: 3 }}>
          Eating during the breaks between the rounds
        </Typography>
        <Typography variant="body1" paragraph>
          You serve the individual courses between the rounds. In this case, of course, your break will
          last not just 10 minutes, but as long as it takes you to eat. You do not have to hurry. An
          order could be, for example, as follows:
        </Typography>

        <ul>
          <li>Appetizer after the prologue (before round 1)</li>
          <li>Appetizer after round 1</li>
          <li>Main course after round 2</li>
          <li>Dessert after round 3</li>
        </ul>

        <Typography variant="body1" paragraph>
          This option works best if you have chosen one of our menu suggestions and you want to show off
          the food to its best advantage. The disadvantage is that you have to be careful not to
          disappear too long in the kitchen when you participate in the Murder Mystery yourself.
        </Typography>

        <Typography variant="h3" sx={{ hyphens: "none", pt: 3, pb: 3 }}>
          Eat during the Murder Mystery
        </Typography>
        <Typography variant="body1" paragraph>
          In this option, you simply eat while discussing the murder case. With a menu, you can serve the
          individual courses either during the rounds or simply when your guests are hungry. This version
          is very flexible and is especially suitable if the food itself has an "event character" or if
          it is finger food, i.e. if you prepare the food together at the table anyway. This applies, for
          example, to fondue, raclette, but also to Asian summer rolls. Or you can make a finger food
          buffet where each guest can bring something.
        </Typography>
      </Container>
    </Box>
  </React.Fragment>
);

Recipes.getLayout = (page) => <NavigationAndFooterLayout>{page}</NavigationAndFooterLayout>;

export default Recipes;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const fields = [
    "*.*",
    "relatedCases.related_Culinario_Mortale_id.*.*",
    "printVersionImages.*",
    "printVersionImages.directus_files_id.mask",
    "printVersionImages.directus_files_id.id",
    "printVersionImages.directus_files_id.description",
    "onlineVersionImages.*",
    "onlineVersionImages.directus_files_id.mask",
    "onlineVersionImages.directus_files_id.id",
    "onlineVersionImages.directus_files_id.description",
  ];

  const { data } = await cmsApiClient.get<{
    data: CmsApiModel.components["schemas"]["ItemsCulinarioMortale"][];
  }>(`/items/Culinario_Mortale?filter={"language": "GB"}&fields=${fields.join(",")}`);

  const products = (
    await Promise.all(
      data.data.map((p) => CmsApiAcl.product(p, "print", { origin: "rezeptideen index" }))
    )
  ).sort((a, b) => {
    const nameA = a.id.toUpperCase(); // ignore upper and lowercase
    const nameB = b.id.toUpperCase(); // ignore upper and lowercase

    // sort in an descending order
    if (nameA < nameB) {
      return 1;
    }
    if (nameA > nameB) {
      return -1;
    }

    // names must be equal
    return 0;
  });

  return {
    props: {
      ...(locale
        ? await serverSideTranslations(locale, ["c-common", "c-user-consent-management"], i18nConfig)
        : {}),
      products,
    },
  };
};
